import {
  AttachmentStoreError,
  verifyAttachmentBytes,
} from '@fabric-harness/sdk';
import type { DirectoryEntry, FilesClient, ListDirectoryResponse } from '@databricks/sdk-files/v2';
import type {
  AttachmentPutInput,
  AttachmentRef,
  AttachmentStore,
  StoredAttachment,
} from '@fabric-harness/sdk';

/**
 * Unity Catalog Volumes attachment backend (v2 migration, workstream C2).
 *
 * Implements the SDK {@link AttachmentStore} contract on a UC Volume via the
 * Databricks Files API, so attachment bytes are governed by Unity Catalog
 * grants like every other Databricks asset. Layout under the volume:
 *
 * ```
 * /Volumes/<catalog>/<schema>/<volume>/<rootPrefix>/<encodeURIComponent(scope)>/<digest[0:2]>/<digest>
 * /Volumes/<catalog>/<schema>/<volume>/<rootPrefix>/<encodeURIComponent(scope)>/<digest[0:2]>/<digest>.json
 * ```
 *
 * The bare `<digest>` file holds the raw bytes; the `<digest>.json` sidecar
 * holds the {@link AttachmentRef} metadata and doubles as the record's
 * existence marker: `put` is first-write-wins per `(scope, digest)` — when the
 * sidecar already exists the put is a no-op, so the first stored ref metadata
 * is retained (contract semantics).
 *
 * Binary transfer and pagination use the official Databricks Files SDK client.
 */
export interface UcVolumesAttachmentStoreOptions {
  client: Pick<
    FilesClient,
    'uploadFile' | 'downloadFile' | 'deleteFile' | 'listDirectoryContents'
  >;
  catalog: string;
  schema: string;
  volume: string;
  /** Directory under the volume root holding all attachments. Default `fh-attachments`. */
  rootPrefix?: string;
}

export class UcVolumesAttachmentStore implements AttachmentStore {
  private readonly client: UcVolumesAttachmentStoreOptions['client'];
  private readonly volumeRoot: string;
  private readonly rootPrefix: string;

  constructor(options: UcVolumesAttachmentStoreOptions) {
    this.client = options.client;
    this.rootPrefix = options.rootPrefix ?? 'fh-attachments';
    this.volumeRoot = `/Volumes/${options.catalog}/${options.schema}/${options.volume}/${this.rootPrefix}`;
  }

  async put(input: AttachmentPutInput): Promise<void> {
    await verifyAttachmentBytes(input.ref, input.bytes);
    const refPath = this.refPath(input.scope, input.ref.digest);
    // First-write-wins per (scope, digest): the sidecar marks a complete record.
    if (await this.readFile(refPath)) return;
    await this.writeFile(this.bytesPath(input.scope, input.ref.digest), input.bytes);
    await this.writeFile(refPath, new TextEncoder().encode(JSON.stringify(input.ref)));
  }

  async get(scope: string, digest: string): Promise<StoredAttachment | null> {
    const refBytes = await this.readFile(this.refPath(scope, digest));
    if (!refBytes) return null;
    const ref = parseAttachmentRef(refBytes);
    const bytes = await this.readFile(this.bytesPath(scope, digest));
    // Sidecar without bytes is an incomplete (interrupted) record — a miss.
    if (!bytes) return null;
    return { ref, bytes };
  }

  async getByAttachmentId(scope: string, attachmentId: string): Promise<StoredAttachment | null> {
    for (const ref of await this.list(scope)) {
      if (ref.id === attachmentId) return this.get(scope, ref.digest);
    }
    return null;
  }

  async list(scope: string): Promise<AttachmentRef[]> {
    const refs: AttachmentRef[] = [];
    for (const prefixDir of await this.listDirectory(this.scopeDir(scope))) {
      if (!prefixDir.isDirectory || !prefixDir.path) continue;
      for (const entry of await this.listDirectory(prefixDir.path)) {
        if (entry.isDirectory || !entry.path || !entry.path.endsWith('.json')) continue;
        const refBytes = await this.readFile(entry.path);
        if (refBytes) refs.push(parseAttachmentRef(refBytes));
      }
    }
    return refs;
  }

  async delete(scope: string, digest: string): Promise<void> {
    // The sidecar is the record marker — delete it first so the record
    // disappears even if the bytes delete below fails.
    if (!(await this.deleteFile(this.refPath(scope, digest)))) {
      throw new AttachmentStoreError('NOT_FOUND', `Attachment not found: ${scope}/${digest}`);
    }
    await this.deleteFile(this.bytesPath(scope, digest));
  }

  // ─── Volume paths ─────────────────────────────────────────────────────────

  private scopeDir(scope: string): string {
    return `${this.volumeRoot}/${encodeURIComponent(scope)}`;
  }

  private bytesPath(scope: string, digest: string): string {
    return `${this.scopeDir(scope)}/${digest.slice(0, 2)}/${digest}`;
  }

  private refPath(scope: string, digest: string): string {
    return `${this.bytesPath(scope, digest)}.json`;
  }

  // ─── Files API ────────────────────────────────────────────────────────────

  private async writeFile(path: string, bytes: Uint8Array): Promise<void> {
    await this.client.uploadFile({
      filePath: path,
      overwrite: true,
      contents: new Blob([viewToBuffer(bytes)]).stream(),
    });
  }

  /** Raw file bytes, or `null` when the file does not exist (404). */
  private async readFile(path: string): Promise<Uint8Array | null> {
    try {
      const response = await this.client.downloadFile({ filePath: path });
      if (!response.contents) return new Uint8Array();
      return new Uint8Array(await new Response(response.contents).arrayBuffer());
    } catch (error) {
      if (isNotFound(error)) return null;
      throw error;
    }
  }

  /** True when the file existed and was deleted; false on 404. */
  private async deleteFile(path: string): Promise<boolean> {
    try {
      await this.client.deleteFile({ filePath: path });
      return true;
    } catch (error) {
      if (isNotFound(error)) return false;
      throw error;
    }
  }

  /** Immediate children of a directory; empty for a missing directory (404). */
  private async listDirectory(path: string): Promise<DirectoryEntry[]> {
    const entries: DirectoryEntry[] = [];
    let pageToken: string | undefined;
    do {
      let listing: ListDirectoryResponse;
      try {
        listing = await this.client.listDirectoryContents({
          directoryPath: path,
          ...(pageToken ? { pageToken } : {}),
        });
      } catch (error) {
        if (isNotFound(error)) return entries;
        throw error;
      }
      entries.push(...(listing.contents ?? []));
      pageToken = listing.nextPageToken;
    } while (pageToken);
    return entries;
  }
}

function isNotFound(error: unknown): boolean {
  if (error && typeof error === 'object' && 'httpStatusCode' in error)
    return (error as { httpStatusCode?: number }).httpStatusCode === 404;
  return /RESOURCE_DOES_NOT_EXIST|does not exist|404/iu.test(String(error));
}

function parseAttachmentRef(bytes: Uint8Array): AttachmentRef {
  const parsed = JSON.parse(new TextDecoder().decode(bytes)) as Partial<AttachmentRef>;
  if (
    typeof parsed.id !== 'string' ||
    typeof parsed.mimeType !== 'string' ||
    typeof parsed.sizeBytes !== 'number' ||
    typeof parsed.digest !== 'string'
  ) {
    throw new Error('databricks volumes: malformed attachment ref sidecar');
  }
  return {
    id: parsed.id,
    mimeType: parsed.mimeType,
    sizeBytes: parsed.sizeBytes,
    digest: parsed.digest,
    ...(typeof parsed.filename === 'string' ? { filename: parsed.filename } : {}),
  };
}

function viewToBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
